import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { X, Megaphone, Calendar, AlertTriangle, Gift, Info, ChevronLeft, ChevronRight } from "lucide-react";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const AccountAnnouncementBanner = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [maxDisplay, setMaxDisplay] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const autoScrollRef = useRef(null);

  useEffect(() => {
    fetchAnnouncements();
    fetchSettings();
  }, []);

  useEffect(() => {
    if (announcements.length <= 1 || isPaused) {
      if (autoScrollRef.current) clearInterval(autoScrollRef.current);
      return;
    }
    autoScrollRef.current = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % announcements.length);
    }, 5000);
    return () => { if (autoScrollRef.current) clearInterval(autoScrollRef.current); };
  }, [announcements.length, isPaused]);

  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${API}/account-announcements`);
      const dismissedIds = JSON.parse(localStorage.getItem("dismissedAccountAnnouncements") || "[]");
      setAnnouncements(res.data.filter(a => !dismissedIds.includes(a.id)));
    } catch (error) {
      console.error("Error fetching account announcements:", error);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/account-announcements/settings`);
      setMaxDisplay(res.data.max_display || 1);
    } catch (error) {
      setMaxDisplay(1);
    }
  };

  const handleDismissAll = () => {
    const allIds = announcements.map(a => a.id);
    const dismissedIds = JSON.parse(localStorage.getItem("dismissedAccountAnnouncements") || "[]");
    localStorage.setItem("dismissedAccountAnnouncements", JSON.stringify([...dismissedIds, ...allIds]));
    setAnnouncements([]);
  };

  const handlePrev = () => setCurrentIndex(prev => prev === 0 ? announcements.length - 1 : prev - 1);
  const handleNext = () => setCurrentIndex(prev => (prev + 1) % announcements.length);

  const getTypeConfig = (type) => {
    switch (type) {
      case "event": return { icon: Calendar, bg: "bg-gradient-to-r from-purple-500 to-indigo-600", iconBg: "bg-purple-400/30" };
      case "promo": return { icon: Gift, bg: "bg-gradient-to-r from-emerald-500 to-teal-600", iconBg: "bg-emerald-400/30" };
      case "alert": return { icon: AlertTriangle, bg: "bg-gradient-to-r from-amber-500 to-orange-600", iconBg: "bg-amber-400/30" };
      default: return { icon: Info, bg: "bg-gradient-to-r from-cyan-500 to-blue-600", iconBg: "bg-cyan-400/30" };
    }
  };

  if (announcements.length === 0) return null;

  const displayAnnouncements = announcements.slice(0, maxDisplay);
  const safeIndex = currentIndex < displayAnnouncements.length ? currentIndex : 0;
  const current = displayAnnouncements[safeIndex];
  if (!current) return null;
  
  const config = getTypeConfig(current.type);
  const Icon = config.icon;

  return (
    <div 
      className={`${config.bg} text-white py-3 px-3 sm:px-4 rounded-lg mb-6`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      data-testid="account-announcement-banner"
    >
      <div className="max-w-7xl mx-auto">
        {/* Mobile Layout */}
        <div className="flex sm:hidden items-start gap-2">
          <button onClick={handleDismissAll} className="p-1.5 hover:bg-white/20 rounded-full transition-colors flex-shrink-0 mt-0.5" aria-label="Dismiss">
            <X className="w-4 h-4" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-base leading-tight">{current.title}</p>
            <div className="text-sm text-white/90 mt-1 [&_a]:underline [&_a]:font-medium line-clamp-3" dangerouslySetInnerHTML={{ __html: current.content }} />
            {displayAnnouncements.length > 1 && (
              <div className="flex items-center gap-2 mt-2">
                <button onClick={handlePrev} className="p-1 hover:bg-white/20 rounded-full"><ChevronLeft className="w-4 h-4" /></button>
                <div className="flex items-center gap-1.5">
                  {displayAnnouncements.map((_, idx) => (
                    <button key={idx} onClick={() => setCurrentIndex(idx)} className={`w-2 h-2 rounded-full transition-colors ${idx === safeIndex ? "bg-white" : "bg-white/40"}`} />
                  ))}
                </div>
                <button onClick={handleNext} className="p-1 hover:bg-white/20 rounded-full"><ChevronRight className="w-4 h-4" /></button>
              </div>
            )}
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden sm:flex items-center gap-4">
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={handleDismissAll} className="p-1.5 hover:bg-white/20 rounded-full transition-colors" aria-label="Dismiss">
              <X className="w-4 h-4" />
            </button>
            {displayAnnouncements.length > 1 && (
              <>
                <button onClick={handlePrev} className="p-1 hover:bg-white/20 rounded-full"><ChevronLeft className="w-4 h-4" /></button>
                <div className="flex items-center gap-1">
                  {displayAnnouncements.map((_, idx) => (
                    <button key={idx} onClick={() => setCurrentIndex(idx)} className={`w-2 h-2 rounded-full transition-colors ${idx === safeIndex ? "bg-white" : "bg-white/40"}`} />
                  ))}
                </div>
                <button onClick={handleNext} className="p-1 hover:bg-white/20 rounded-full"><ChevronRight className="w-4 h-4" /></button>
              </>
            )}
          </div>
          <div className="flex items-center gap-3 flex-1 min-w-0 overflow-hidden">
            <div className={`${config.iconBg} p-2 rounded-full flex-shrink-0`}><Icon className="w-5 h-5" /></div>
            <div className="min-w-0 flex-1 overflow-hidden">
              <p className="font-semibold text-sm md:text-base truncate">{current.title}</p>
              <div className="text-sm text-white/90 [&_a]:underline [&_a]:font-medium max-h-16 overflow-y-auto" dangerouslySetInnerHTML={{ __html: current.content }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountAnnouncementBanner;
